// phoneValidation.js
//
// Phone-number normalisation and validation tuned for Ghana, with a
// permissive fallback for international numbers (clients abroad,
// diaspora staff, etc.).
//
// Why a library: the firm enters phone numbers everywhere — employee
// invites, contacts, firm profile, conflict checks, intake form. We
// were storing whatever the user typed (`+233 24 123 4567`, `024 123
// 4567`, `0241234567`, `233241234567`, ` 0241234567 ` with a stray
// trailing space), which makes:
//
//   * Search useless ("find clients by phone 0241234567" misses the
//     ones stored with the +233 prefix)
//   * Dedup impossible ("is +233 24 123 4567 the same person as
//     0241234567?" — yes, but the DB doesn't know that)
//   * Bulk SMS / call flows brittle
//
// What this file does:
//   1. `normalizeGhanaPhone(raw)` — strips spaces / dashes / parens,
//      converts +233 / 233 / 0XX prefixes to a canonical E.164
//      `+233XXXXXXXXX`. Returns null if the input doesn't look like
//      a Ghana number (no auto-conversion — keeps non-Ghana inputs
//      untouched so the diaspora case still works).
//   2. `validatePhone(raw)` — returns `{ valid, normalized, error }`.
//      Accepts Ghana mobile (020/023/024/025/026/027/028/050/053/054/
//      055/056/057/059), Ghana landline (03X), AND generic
//      international (+XX...XX, 7-15 digits) for non-Ghana numbers.
//   3. `formatGhanaPhone(e164)` — display helper. `+233241234567` →
//      `+233 24 123 4567`.

const STRIP_RE = /[\s\-()./]/g;

// Mobile prefixes per the NCA frequency plan (current as of 2024).
// Source: National Communications Authority Ghana, "Numbering Plan
// for Ghana" — public document.
const MOBILE_PREFIXES = [
  '20', '23', '24', '25', '26', '27', '28',  // historic + MTN / Vodafone
  '50', '53', '54', '55', '56', '57', '59',  // newer ranges
];

// Landline area codes — first two digits after the leading 0.
// Not exhaustive (NCA reassigns periodically); covers the major
// commercial centres a firm is likely to encounter.
const LANDLINE_PREFIXES = [
  '30', '31', '32', '33', '34', '35', '36', '37', '38', '39',
];

const ALL_GH_PREFIXES = new Set([...MOBILE_PREFIXES, ...LANDLINE_PREFIXES]);

/**
 * Strip formatting from a raw input.
 * `' (+233) 024-123 4567 '` → `'+233241234567'`
 */
function stripFormatting(raw) {
  return String(raw ?? '').replace(STRIP_RE, '');
}

/**
 * Canonicalize a Ghana phone number to E.164.
 *
 * Accepts:
 *   '+233241234567'   → '+233241234567'
 *   '233241234567'    → '+233241234567'
 *   '0241234567'      → '+233241234567'
 *   '241234567'       → '+233241234567'
 *
 * Returns null when the input isn't a Ghana number (so non-Ghana
 * numbers — diaspora clients, foreign correspondents — are NOT
 * silently mangled). Callers should fall back to validating against
 * the generic-international rule in that case.
 */
export function normalizeGhanaPhone(raw) {
  const s = stripFormatting(raw);
  if (!s) return null;

  let digits;
  if (s.startsWith('+233')) {
    digits = s.slice(4);
  } else if (s.startsWith('233') && s.length === 12) {
    digits = s.slice(3);
  } else if (s.startsWith('0') && s.length === 10) {
    digits = s.slice(1);
  } else if (/^\d{9}$/.test(s)) {
    // Bare 9-digit (e.g. `241234567`) — assume Ghana.
    digits = s;
  } else {
    return null;
  }

  // After stripping the country code / leading 0, we should have 9
  // digits whose first 2 are a known Ghana prefix.
  if (!/^\d{9}$/.test(digits)) return null;
  const prefix = digits.slice(0, 2);
  if (!ALL_GH_PREFIXES.has(prefix)) return null;

  return `+233${digits}`;
}

/**
 * Generic international fallback. Used when the input clearly isn't
 * Ghana (starts with a non-+233 country code, or is the right length
 * for an E.164 number outside Ghana). Loose by design — we can't
 * validate every country's numbering plan, but we can reject obvious
 * garbage like "1234" or strings with letters.
 */
function isPlausibleInternational(raw) {
  const s = stripFormatting(raw);
  // E.164 max is 15 digits, min realistically 7 (some short-code-style
  // numbers exist but a firm/client phone won't be that short).
  if (!/^\+?\d{7,15}$/.test(s)) return false;
  // Reject "0..." prefixed numbers that aren't valid Ghana — they're
  // not international format (a real international number starts with +).
  if (s.startsWith('0')) return false;
  return true;
}

/**
 * Full validation result.
 *
 *   { valid: true,  normalized: '+233241234567', error: null,        country: 'GH' }
 *   { valid: true,  normalized: '+14155552671',  error: null,        country: 'INTL' }
 *   { valid: false, normalized: null,            error: 'Enter a valid phone number (e.g. 024 123 4567).' }
 */
export function validatePhone(raw, { required = true } = {}) {
  const s = String(raw ?? '').trim();
  if (!s) {
    return required
      ? { valid: false, normalized: null, error: 'Phone number is required.', country: null }
      : { valid: true,  normalized: null, error: null, country: null };
  }

  // Ghana first.
  const gh = normalizeGhanaPhone(s);
  if (gh) {
    return { valid: true, normalized: gh, error: null, country: 'GH' };
  }

  // Fallback for non-Ghana numbers.
  if (isPlausibleInternational(s)) {
    const intl = stripFormatting(s).startsWith('+')
      ? stripFormatting(s)
      : `+${stripFormatting(s)}`;
    return { valid: true, normalized: intl, error: null, country: 'INTL' };
  }

  return {
    valid: false,
    normalized: null,
    error: 'Enter a valid phone number (e.g. 024 123 4567 or +233 24 123 4567).',
    country: null,
  };
}

/**
 * Display formatter for Ghana E.164 numbers.
 * `+233241234567` → `+233 24 123 4567`
 * Non-Ghana strings pass through unchanged (caller can format with
 * their own locale if they care).
 */
export function formatGhanaPhone(e164) {
  if (typeof e164 !== 'string') return '';
  if (!e164.startsWith('+233') || e164.length !== 13) return e164;
  const rest = e164.slice(4); // 9 digits
  return `+233 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
}
